import { CustomEndpointService } from "@app/services/CustomEndpointService";
import { CustomEndpointChatService } from "@app/services/CustomEndpointChatService";
import { RoutesEnum } from "@app/models/RoutesEnum";
import { CustomEndpointInterface } from "@dashart/dashart-gpt-shared-library";
import ProgressSpinnerOverlay from "@view/components/progressSpinnerOverlay/ProgressSpinnerOverlay";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import ChatScreenHoc from "./ChatScreenHoc";

const PUBLIC_ID_PATTERN = /^[a-f0-9]{32}$/;

/**
 * Route wrapper that resolves a CustomEndpoint from the `:publicId` URL param
 * and renders ChatScreenHoc with a per-endpoint CustomEndpointChatService.
 *
 * Validates the publicId format client-side, loads the user's endpoints, and
 * navigates back to the list screen if the id is malformed or unknown.
 *
 * @returns the chat screen when an endpoint resolves, a spinner while loading,
 *          or a Navigate to the list on failure
 */
const CustomEndpointChatRoute: React.FunctionComponent = () => {
    const { publicId } = useParams();
    const [endpoint, setEndpoint] = useState<CustomEndpointInterface | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!publicId || !PUBLIC_ID_PATTERN.test(publicId)) {
            setFailed(true);
            setLoading(false);
            return;
        }
        CustomEndpointService.getAll().then(
            (endpoints) => {
                const found = endpoints.find((e) => e.publicId === publicId) ?? null;
                if (!found) {
                    setFailed(true);
                } else {
                    setEndpoint(found);
                }
                setLoading(false);
            },
            () => { setFailed(true); setLoading(false); }
        );
    }, [publicId]);

    const chatService = useMemo(
        () => endpoint ? new CustomEndpointChatService(endpoint) : null,
        [endpoint?.publicId]
    );

    if (loading) return <ProgressSpinnerOverlay visible={true} />;
    if (failed || !endpoint || !chatService) return <Navigate to={RoutesEnum.customEndpointList} />;

    return <ChatScreenHoc chatService={chatService} />;
};

export default CustomEndpointChatRoute;
